import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Tooltip, Snackbar, Alert } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import { PostData } from '../model/types';
import { CacheContext, CacheContextType } from '../context/CacheContext';

/**
 * SHARE TOMBSTONE — generates a Wordle-style shareable pixel-art death card
 * for an expired post. The whole point is the emoji-grid viral loop: the
 * card is small enough to paste into a tweet and interesting enough that
 * people will click through to wipe.haus.
 */

function formatLived(fromISO: string, toISO: string): string {
  const from = DateTime.fromISO(fromISO);
  const to = DateTime.fromISO(toISO);
  const diff = to.diff(from, ['days', 'hours', 'minutes']).toObject();
  const d = Math.floor(diff.days ?? 0);
  const h = Math.floor(diff.hours ?? 0);
  const m = Math.floor(diff.minutes ?? 0);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number, maxLines = 3): number {
  const words = text.split(' ');
  let line = '';
  let lineCount = 0;
  for (let i = 0; i < words.length; i++) {
    const testLine = line + words[i] + ' ';
    const { width } = ctx.measureText(testLine);
    if (width > maxWidth && i > 0) {
      ctx.fillText(line.trim(), x, y + lineCount * lineHeight);
      line = words[i] + ' ';
      lineCount += 1;
      if (lineCount >= maxLines - 1) {
        // Last line: truncate remainder with ellipsis
        const remainder = words.slice(i).join(' ');
        const { width: rWidth } = ctx.measureText(remainder);
        if (rWidth > maxWidth) {
          let cut = remainder;
          while (ctx.measureText(cut + '…').width > maxWidth && cut.length > 0) cut = cut.slice(0, -1);
          ctx.fillText(cut + '…', x, y + lineCount * lineHeight);
        } else {
          ctx.fillText(remainder, x, y + lineCount * lineHeight);
        }
        return lineCount + 1;
      }
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line.trim(), x, y + lineCount * lineHeight);
  return lineCount + 1;
}

function drawTombstoneCard(canvas: HTMLCanvasElement, opts: {
  username: string;
  postText: string;
  createdAt: string;
  expiredAt: string;
  votes: number;
  replies: number;
  postId: string;
}) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;

  // Background — deep purple night sky gradient
  const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
  bgGrad.addColorStop(0, '#0a0a18');
  bgGrad.addColorStop(1, '#1a0a2a');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // Stars (deterministic pseudo-random based on postId)
  let seed = 0;
  for (let i = 0; i < opts.postId.length; i++) seed = (seed * 31 + opts.postId.charCodeAt(i)) >>> 0;
  const rand = () => {
    seed = (seed * 1103515245 + 12345) >>> 0;
    return (seed >>> 16) / 65535;
  };
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  for (let i = 0; i < 60; i++) {
    const x = Math.floor(rand() * W);
    const y = Math.floor(rand() * (H * 0.55));
    const size = rand() > 0.85 ? 2 : 1;
    ctx.fillRect(x, y, size, size);
  }

  // Moon
  ctx.fillStyle = 'rgba(255,255,220,0.9)';
  ctx.beginPath();
  ctx.arc(W - 80, 80, 28, 0, Math.PI * 2);
  ctx.fill();

  // Header band
  ctx.fillStyle = '#b51e1b';
  ctx.font = 'bold 44px "Courier New", monospace';
  ctx.textAlign = 'center';
  ctx.fillText('⚰ R.I.P.', W / 2, 70);

  // Username
  ctx.fillStyle = '#4caf50';
  ctx.font = 'bold 28px "Courier New", monospace';
  ctx.fillText(`@${opts.username}`, W / 2, 115);

  // Quote
  ctx.fillStyle = '#dddddd';
  ctx.font = 'italic 18px "Courier New", monospace';
  const cleanText = (opts.postText ?? '')
    .replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
    .replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1')
    .replace(/https?:\/\/\S+/g, '')
    .trim();
  wrapText(ctx, `"${cleanText}"`, W / 2, 155, W - 60, 24, 3);

  // Tombstone shape — centered
  const tx = W / 2;
  const ty = 260;
  const tw = 200;
  const tharch = 100;
  const threct = 160;

  // Tombstone body
  ctx.fillStyle = '#3a3a4a';
  ctx.strokeStyle = '#6a6a7a';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(tx - tw / 2, ty + tharch);
  ctx.lineTo(tx - tw / 2, ty + tharch + threct);
  ctx.lineTo(tx + tw / 2, ty + tharch + threct);
  ctx.lineTo(tx + tw / 2, ty + tharch);
  ctx.arc(tx, ty + tharch, tw / 2, 0, Math.PI, true);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // Carved dates on tombstone
  const created = DateTime.fromISO(opts.createdAt).toFormat('MMM d');
  const died = DateTime.fromISO(opts.expiredAt).toFormat('MMM d');
  ctx.fillStyle = '#aaaaaa';
  ctx.font = 'bold 20px "Courier New", monospace';
  ctx.fillText(created, tx, ty + 120);
  ctx.fillText('—', tx, ty + 150);
  ctx.fillText(died, tx, ty + 180);

  // Lived time carved below the dash
  ctx.fillStyle = '#888888';
  ctx.font = '16px "Courier New", monospace';
  ctx.fillText(`lived ${formatLived(opts.createdAt, opts.expiredAt)}`, tx, ty + 215);

  // Ground
  ctx.fillStyle = '#2a1a1a';
  ctx.fillRect(0, ty + tharch + threct, W, H - (ty + tharch + threct));
  // Grass bumps
  ctx.fillStyle = '#1a3a1a';
  for (let i = 0; i < 40; i++) {
    const gx = Math.floor(rand() * W);
    const gy = ty + tharch + threct + Math.floor(rand() * 15);
    ctx.fillRect(gx, gy, 3, 2);
  }

  // Stats row
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 20px "Courier New", monospace';
  const statsY = H - 60;
  ctx.fillText(`⬆ ${opts.votes}  ·  💬 ${opts.replies}`, W / 2, statsY);

  // Footer
  ctx.fillStyle = '#888888';
  ctx.font = '16px "Courier New", monospace';
  ctx.fillText('wipe.haus', W / 2, H - 25);
}

function ShareTombstone({ post }: { post: PostData }) {
  const getUserRecord = useContext<CacheContextType>(CacheContext).getUserRecord;
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const author = getUserRecord(post.user_record_id);

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    drawTombstoneCard(canvasRef.current, {
      username: author?.username ?? '???',
      postText: post.value ?? '',
      createdAt: post.created_at,
      expiredAt: post.expire_at,
      votes: post.vote_count ?? 0,
      replies: post.reply_count ?? 0,
      postId: post.id,
    });
  }, [open, author?.username, post.value, post.created_at, post.expire_at, post.vote_count, post.reply_count, post.id]);

  const getBlob = (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (!canvasRef.current) return resolve(null);
      canvasRef.current.toBlob((b) => resolve(b), 'image/png');
    });
  };

  const handleDownload = async () => {
    const blob = await getBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wipe-tombstone-${post.id.slice(0, 8)}.png`;
    a.click();
    URL.revokeObjectURL(url);
    setToast('Downloaded!');
  };

  const handleCopy = async () => {
    const blob = await getBlob();
    if (!blob) return;
    try {
      await (navigator.clipboard as any).write([new (window as any).ClipboardItem({ 'image/png': blob })]);
      setToast('Copied to clipboard!');
    } catch (e) {
      setToast('Copy not supported — try download');
    }
  };

  const handleShare = async () => {
    const blob = await getBlob();
    if (!blob) return;
    const file = new File([blob], `wipe-tombstone.png`, { type: 'image/png' });
    const shareText = `⚰️ @${author?.username ?? '???'}'s post died on wipe.haus · lived ${formatLived(post.created_at, post.expire_at)} · ⬆${post.vote_count ?? 0}`;
    const shareUrl = `${window.location.origin}/posts/${post.id}`;
    try {
      if ((navigator as any).canShare?.({ files: [file] })) {
        await (navigator as any).share({ files: [file], text: shareText, url: shareUrl });
      } else if (navigator.share) {
        await navigator.share({ text: shareText, url: shareUrl });
      } else {
        // Fallback: open tweet intent
        window.open(
          `https://x.com/intent/tweet?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
          '_blank',
          'noopener,noreferrer',
        );
      }
    } catch { /* cancelled */ }
  };

  return (
    <>
      <Tooltip title="Share tombstone">
        <Button
          size="small"
          onClick={() => setOpen(true)}
          startIcon={<IosShareIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            fontFamily: '"VT323", monospace',
            letterSpacing: 1,
            fontSize: '0.85rem',
            color: 'text.secondary',
            mt: 0.5,
          }}
        >
          SHARE TOMBSTONE
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, bgcolor: 'background.default' } }}
      >
        <DialogContent sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
            <Box sx={{ fontFamily: '"VT323", monospace', letterSpacing: 2, fontSize: '1.1rem' }}>
              TOMBSTONE
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <canvas
              ref={canvasRef}
              width={600}
              height={600}
              style={{
                width: '100%',
                maxWidth: 480,
                height: 'auto',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<IosShareIcon />} onClick={handleShare}>
              Share
            </Button>
            <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
              Copy
            </Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
              Download
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ShareTombstone;
